<template>
  <div class="relative">
    <!-- Navbar horizontale -->
    <nav class="w-full flex justify-between items-center h-16 shadow-md md:hidden px-8 transition-colors duration-200"
      :class="isDark ? 'bg-zinc-950 border-t border-zinc-900' : 'bg-white'">

      <!-- Menu Drawer -->
      <button @click="toggleDrawer" class="flex flex-col items-center justify-center focus:outline-none transition-colors"
        :class="isDark ? 'text-zinc-400' : 'text-zinc-600'">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        <span class="text-xs mt-1">Menu</span>
      </button>

      <!-- Logo au centre -->
      <img v-if="isDark" src="../assets/Bookmysalon_White.svg" class="h-8 w-auto" alt="Logo" />
      <img v-else src="../assets/Bookmysalon_Black.svg" class="h-8 w-auto" alt="Logo" />

      <!-- Theme Toggle -->
      <ThemeToggle />
    </nav>

    <!-- Drawer / Volet -->
    <transition name="slide">
      <aside v-if="isDrawerOpen" class="fixed inset-0 z-40 flex">
        <div class="fixed inset-0 bg-black bg-opacity-50" @click="toggleDrawer"></div>
        <div class="relative w-72 h-full shadow-lg flex flex-col overflow-y-auto transition-colors duration-200"
          :class="isDark ? 'bg-zinc-950' : 'bg-white'">
          <div class="p-4 flex justify-between items-center transition-colors duration-200"
            :class="isDark ? 'border-b border-zinc-900' : 'border-b border-gray-200'">
            <div class="flex items-center gap-3">
              <img v-if="isDark" src="../assets/Bookmysalon_White.svg" class="h-10 rounded-lg" alt="Logo" />
              <img v-else src="../assets/Bookmysalon_Black.svg" class="h-10 rounded-lg" alt="Logo" />
            </div>
            <button @click="toggleDrawer" class="transition-colors"
              :class="isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'">
              ✕
            </button>
          </div>

          <!-- Navigation -->
          <nav class="flex-1 px-3 py-6 space-y-6 overflow-y-auto">
            <!-- Dashboard -->
            <div class="space-y-1">
              <h3 class="text-[9px] font-medium uppercase tracking-widest px-3 mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Pilotage
              </h3>
              <RouterLink @click="toggleDrawer" to="/"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
                <span>Dashboard</span>
              </RouterLink>
            </div>

            <!-- Rendez-vous -->
            <div v-if="hasPermission('planning')" class="space-y-1">
              <h3 class="text-[9px] font-medium uppercase tracking-widest px-3 mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Flux & Agendas
              </h3>
              <RouterLink @click="toggleDrawer" to="/appointments"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                <span>Rendez-vous</span>
              </RouterLink>
            </div>

            <!-- Clients -->
            <div v-if="hasPermission('clients')" class="space-y-1">
              <h3 class="text-[9px] font-medium uppercase tracking-widest px-3 mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Comptes Clients
              </h3>
              <RouterLink @click="toggleDrawer" to="/clients"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
                <span>Clients</span>
              </RouterLink>
            </div>

            <!-- Services / Prestations -->
            <div v-if="hasAnyPermission(['prestations', 'services', 'categories'])" class="space-y-1">
              <h3 class="text-[9px] font-medium uppercase tracking-widest px-3 mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Catalogue Métier
              </h3>
              <RouterLink @click="toggleDrawer" to="/services"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581a2.25 2.25 0 003.182 0l5.171-5.17a2.25 2.25 0 000-3.181L12.012 3.659A2.25 2.25 0 0010.415 3zM6.75 6.75h.008v.008H6.75V6.75z" />
                </svg>
                <span>Services / Prestations</span>
              </RouterLink>
            </div>

            <!-- Staff -->
            <div v-if="accountType !== 'solo' && hasPermission('collaborateurs')" class="space-y-1">
              <h3 class="text-[9px] font-medium uppercase tracking-widest px-3 mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Ressources & Staff
              </h3>
              <RouterLink @click="toggleDrawer" to="/staff"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                <span>Staff / Employés</span>
              </RouterLink>
            </div>

            <!-- Finances -->
            <div v-if="hasPermission('caisse')" class="space-y-1">
              <h3 class="text-[9px] font-medium uppercase tracking-widest px-3 mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Comptabilité
              </h3>
              <RouterLink @click="toggleDrawer" to="/finance"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75m0 .75h-.75m.75 0v1.5m0 6.75v.75m0 .75h-.75m.75 0V13.5m0-2.25H4.5a.75.75 0 000-1.5h-.75m0-3h.75A.75.75 0 005.25 6h-.75m13.5 10.5a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zM6.75 18h10.5a.75.75 0 00.75-.75V3.75a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v13.5c0 .414.336.75.75.75z" />
                </svg>
                <span>Finances</span>
              </RouterLink>
            </div>

            <!-- Marketing -->
            <div v-if="hasAnyPermission(['promotions', 'fidelite'])" class="space-y-1">
              <h3 class="text-[9px] font-medium uppercase tracking-widest px-3 mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Marketing
              </h3>
              <RouterLink v-if="hasPermission('fidelite')" @click="toggleDrawer" to="/promotions-fidelite"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 568l3-3m0 0l3-3m-3 3V7.5M12 15v-3m0 0l3-3m-3 3l-3-3m12 6.75a6 6 0 11-12 0 6 6 0 0112 0z" />
                </svg>
                <span>Fidélité</span>
              </RouterLink>
              <RouterLink v-if="hasPermission('promotions')" @click="toggleDrawer" to="/promotions"
                class="flex items-center gap-3 rounded-xl text-xs px-3 py-2 transition-all duration-150"
                :active-class="isDark ? 'bg-zinc-900 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-medium'"
                :class="isDark ? 'hover:bg-zinc-900/40 text-zinc-400' : 'hover:bg-zinc-50 text-zinc-600'">
                <svg class="w-4 h-4 shrink-0 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581a2.25 2.25 0 003.182 0l5.171-5.17a2.25 2.25 0 000-3.181L12.012 3.659A2.25 2.25 0 0010.415 3zM6.75 6.75h.008v.008H6.75V6.75z" />
                </svg>
                <span>Promotions</span>
              </RouterLink>
            </div>
          </nav>

          <!-- Profil utilisateur -->
          <div class="px-4 py-3 transition-colors duration-200"
            :class="isDark ? 'border-t border-zinc-900 bg-zinc-950' : 'border-t border-gray-200 bg-gray-50'">
            <RouterLink @click="toggleDrawer" to="/settings" class="flex items-center gap-3">
              <img
                :src="user.photo || 'https://ui-avatars.com/api/?background=f4f4f5&color=18181b&name=' + user.nom + '+' + user.prenom"
                alt="Photo de profil"
                class="w-12 h-12 rounded-full object-cover shadow-sm border transition-colors"
                :class="isDark ? 'border-zinc-800' : 'border-gray-200'"
              />
              <div>
                <p class="text-sm font-semibold leading-tight transition-colors"
                  :class="isDark ? 'text-white' : 'text-gray-800'">
                  {{ user.prenom }} {{ user.nom }}
                </p>
                <p class="text-xs truncate max-w-[140px] transition-colors"
                  :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                  {{ user.email }}
                </p>
              </div>
            </RouterLink>
          </div>

          <!-- Déconnexion -->
          <div class="p-4 space-y-3 transition-colors duration-200"
            :class="isDark ? 'border-t border-zinc-900' : 'border-t border-gray-200'">
            <button
              @click="logout"
              class="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition font-medium shadow-sm"
              :class="isDark
                ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-800'
                : 'bg-red-600 hover:bg-red-700 text-white'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useTheme } from '@/composables/useTheme'
import { usePermissions } from '@/composables/usePermissions'
import ThemeToggle from './ThemeToggle.vue'

const router = useRouter()
const { isDark } = useTheme()
const { hasPermission, hasAnyPermission, loadPermissions } = usePermissions()

// Charger les permissions
loadPermissions()

const isDrawerOpen = ref(false)
const accountType = ref(localStorage.getItem('accountType') || 'solo')

// Charger les infos utilisateur
const storedUser = JSON.parse(localStorage.getItem("user") || "{}")
const user = ref({
  prenom: storedUser.prenom || "Utilisateur",
  nom: storedUser.nom || "",
  email: storedUser.email || "",
  photo: storedUser.photo || null
})

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value
}

const logout = () => {
  localStorage.removeItem("token")
  localStorage.removeItem("user")
  router.push("/login")
}
</script>

<style scoped>
.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter-from {
  transform: translateX(-100%);
}
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
